import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { FullScreenLoader } from "@/components/auth/AdminGate";
import ShareDialog from "@/components/common/ShareDialog";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { EditorCtx, useEditorState, pagePath } from "@/components/editor/useEditorState";
import Topbar from "@/components/editor/Topbar";
import LeftSidebar from "@/components/editor/LeftSidebar";
import Canvas from "@/components/editor/Canvas";
import RightPanel from "@/components/editor/RightPanel";
import AddSectionDialog from "@/components/editor/AddSectionDialog";
import VersionsSheet from "@/components/editor/VersionsSheet";
import SimulatedModules from "@/components/editor/SimulatedModules";
import BriefAssistant from "@/components/editor/BriefAssistant";
import GenerationOverlay from "@/components/editor/GenerationOverlay";
import GenerateBanner from "@/components/editor/GenerateFlow";
import VisualReviewSheet from "@/components/review/VisualReviewSheet";
import { errMsg } from "@/lib/api";
import { useMedia } from "@/hooks/use-media";

// keyed by id: opening another project (e.g. a fresh duplicate) remounts the editor with clean state
export default function Editor() {
  const { id } = useParams();
  return <EditorView key={id} />;
}

function EditorView() {
  const { id } = useParams();
  const ed = useEditorState(id);
  const isDesktop = useMedia("(min-width: 1024px)");
  const frameRef = useRef(null);
  const lastCapture = useRef(0);
  const [ui, setUiState] = useState({ device: window.matchMedia("(max-width: 639px)").matches ? "mobile" : "desktop", selectMode: false, rightTab: "props", leftTab: "sections", fullscreen: false, expanded: false, versions: false, review: false, reviewStatus: null, share: false, addSection: null, leftOpen: false, rightOpen: false, confirm: null });
  const setUi = useCallback((patch) => setUiState((u) => ({ ...u, ...Object.fromEntries(Object.entries(patch).filter(([, v]) => v !== undefined)) })), []);
  const [loadError, setLoadError] = useState(null);
  const { load, content, project, api } = ed;

  useEffect(() => { load().then((p) => { document.title = `${p.name} · MUSORA`; }).catch((e) => setLoadError(errMsg(e))); }, [load]);
  useEffect(() => {
    const h = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key === "s") { e.preventDefault(); ed.save().then((ok) => ok && toast.success("Salvato")); return; }
      const k = e.key.toLowerCase();
      if (!mod || (k !== "z" && k !== "y")) return;
      const t = e.target;
      if (t.closest?.(".cm-editor") || ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName) || t.isContentEditable) return;
      e.preventDefault();
      if (k === "y" || e.shiftKey) ed.redo(); else ed.undo();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [ed.save, ed.undo, ed.redo]); // eslint-disable-line react-hooks/exhaustive-deps

  const goToPage = useCallback((pid) => {
    ed.setPageId(pid);
    const c = ed.content; const p = c?.pages.find((x) => x.id === pid);
    if (p) frameRef.current?.navigate(pagePath(c, p).replace(/:[^/]+/g, "demo"));
  }, [ed.content]); // eslint-disable-line react-hooks/exhaustive-deps

  const onCapture = useCallback(async (m) => {
    if (!m?.dataUrl) { if (m?.error) console.warn("thumbnail", m.error); return; }
    try { const { data } = await api.post(`/projects/${id}/thumbnail`, { data_url: m.dataUrl }); ed.setProject((p) => ({ ...p, thumbnail_url: data.thumbnail_url })); } catch { /* non-blocking */ }
  }, [api, id]); // eslint-disable-line react-hooks/exhaustive-deps
  const captureNow = () => {
    if (ed.pageId !== content.home_page_id) { goToPage(content.home_page_id); setTimeout(() => frameRef.current?.capture(), 1500); }
    else frameRef.current?.capture();
    toast.message("Miniatura in aggiornamento…");
  };

  useEffect(() => {
    if (!content || !project || ed.saveState !== "saved" || ui.device !== "desktop" || ed.pageId !== content.home_page_id) return undefined;
    if (project.generation?.status === "running") return undefined;
    const home = content.pages.find((p) => p.id === content.home_page_id);
    if (!home?.sections.length || (project.thumbnail_url && Date.now() - lastCapture.current < 180000)) return undefined;
    const t = setTimeout(() => { lastCapture.current = Date.now(); frameRef.current?.capture(); }, 3000);
    return () => clearTimeout(t);
  }, [ed.saveState, ed.pageId, ui.device, content, project?.thumbnail_url, project?.generation?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loadError) return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-10" data-testid="editor-load-error">
      <div className="text-center space-y-3">
        <p className="text-sm text-slate-700">{loadError}</p>
        <Link to="/" className="inline-block rounded-md bg-slate-900 px-4 py-2 text-sm text-white hover:bg-slate-700" data-testid="editor-back-to-projects">Torna ai progetti</Link>
      </div>
    </div>
  );
  if (!content || !project) return <FullScreenLoader label="Apertura editor…" />;

  const onStatus = async (s) => {
    try { await api.patch(`/projects/${id}`, { status: s }); ed.setProject((p) => ({ ...p, status: s })); toast.success("Stato aggiornato"); } catch (e) { toast.error(errMsg(e)); }
  };
  const ctx = { ...ed, ui, setUi, frameRef, goToPage, isDesktop };
  const rightW = ui.expanded ? "w-[min(760px,55vw)]" : "w-[360px]";

  return (
    <EditorCtx.Provider value={ctx}>
      <div className="h-screen flex flex-col bg-slate-50 overflow-hidden" data-testid="editor-page">
        <Topbar onStatus={onStatus} onSaveNow={() => ed.save().then((ok) => ok && toast.success("Salvato"))} />
        <div className="flex-1 min-h-0 flex">
          {isDesktop && <aside className="w-[300px] shrink-0">{<LeftSidebar onCapture={captureNow} />}</aside>}
          <div className="relative flex-1 min-w-0 flex"><Canvas onCapture={onCapture} /><GenerationOverlay /><GenerateBanner /><SimulatedModules /><BriefAssistant /></div>
          {isDesktop && <aside className={`${rightW} shrink-0 transition-[width] duration-200`}><RightPanel /></aside>}
        </div>
        {!isDesktop && (
          <>
            <Sheet open={ui.leftOpen} onOpenChange={(o) => setUi({ leftOpen: o })}>
              <SheetContent side="left" className="p-0 w-[320px] max-w-[90vw]"><SheetTitle className="sr-only">Struttura</SheetTitle><LeftSidebar onCapture={captureNow} /></SheetContent>
            </Sheet>
            <Sheet open={ui.rightOpen} onOpenChange={(o) => setUi({ rightOpen: o })}>
              <SheetContent side="right" className="p-0 w-[380px] max-w-[94vw]"><SheetTitle className="sr-only">Proprietà</SheetTitle><RightPanel /></SheetContent>
            </Sheet>
          </>
        )}
        <AddSectionDialog />
        <VersionsSheet />
        <VisualReviewSheet />
        <ShareDialog project={project} open={ui.share} onOpenChange={(o) => setUi({ share: o })} beforePublish={ed.save} afterPublish={async () => { await ed.load(); window.dispatchEvent(new Event("adm:project-changed")); }}
          warning={ed.diagnostics.length ? `${ed.diagnostics.length} ${ed.diagnostics.length === 1 ? "elemento ha" : "elementi hanno"} errori di codice e non ${ed.diagnostics.length === 1 ? "sarà visibile" : "saranno visibili"} nella demo pubblica.` : null}
          onChange={(upd) => ed.setProject((p) => ({ ...p, ...upd }))} />
        <ConfirmDialog open={!!ui.confirm} onOpenChange={(o) => !o && setUi({ confirm: null })} destructive confirmLabel="Elimina"
          title={ui.confirm?.title} description={ui.confirm?.description} onConfirm={() => { ui.confirm?.onConfirm(); setUi({ confirm: null }); }} />
      </div>
    </EditorCtx.Provider>
  );
}
